import {
  CommunitiesRoutes,
  NetworksRoutes,
  PoolingRoutes,
  StaticPagesRoutes,
  UserRoutes,
} from '@/routes/_hooks/routes.enum'
import { generatePath } from '@/routing'

type ContextKind = 'user' | 'circle' | 'network'

const getContextKind = (contextIri: string | null): ContextKind => {
  if (!contextIri) return 'user'
  if (contextIri.includes('/circles/')) return 'circle'
  if (contextIri.includes('/networks/')) return 'network'
  return 'user'
}

/** Slug segment from a stored circle or network IRI (e.g. `/circles/foo` → `foo`). */
export const getSlugFromContextIri = (
  contextIri: string | null
): string | null => {
  if (!contextIri) return null
  const match = contextIri.match(/\/(?:circles|networks)\/([^/]+)/)
  return match?.[1] ?? null
}

const getTargetContextRoutePrefix = (
  kind: ContextKind
): '/c' | '/n' | '/my' => {
  if (kind === 'circle') return '/c'
  if (kind === 'network') return '/n'
  return '/my'
}

const toTargetDashboard = (
  targetKind: ContextKind,
  targetSlug: string | null
): string => {
  if (targetKind === 'circle' && targetSlug) {
    return generatePath(CommunitiesRoutes.Dashboard, { slug: targetSlug })
  }
  if (targetKind === 'network' && targetSlug) {
    return generatePath(NetworksRoutes.Dashboard, { slug: targetSlug })
  }
  return StaticPagesRoutes.Home
}

const mapUserRouteToContextRoute = (
  pathname: string,
  targetKind: ContextKind,
  targetSlug: string | null
): string | null => {
  if (!targetSlug || targetKind === 'user') {
    return null
  }

  if (pathname === PoolingRoutes.MyMaterials) {
    return targetKind === 'circle'
      ? generatePath(CommunitiesRoutes.Materials, { slug: targetSlug })
      : generatePath(NetworksRoutes.Materials, { slug: targetSlug })
  }

  if (pathname === PoolingRoutes.AddFromTemplates) {
    return targetKind === 'circle'
      ? generatePath(CommunitiesRoutes.AddFromTemplates, { slug: targetSlug })
      : generatePath(NetworksRoutes.AddFromTemplates, { slug: targetSlug })
  }

  if (pathname === PoolingRoutes.MyBookingAsOwner) {
    return targetKind === 'circle'
      ? generatePath(CommunitiesRoutes.BookingAsOwner, { slug: targetSlug })
      : generatePath(NetworksRoutes.BookingAsOwner, { slug: targetSlug })
  }

  if (pathname === PoolingRoutes.MyBookingRequests) {
    return targetKind === 'circle'
      ? generatePath(CommunitiesRoutes.BookingRequests, { slug: targetSlug })
      : generatePath(NetworksRoutes.BookingRequests, { slug: targetSlug })
  }

  const bookingMatch = pathname.match(/^\/my\/bookings\/([^/]+)$/)
  if (bookingMatch?.[1]) {
    const bookingId = bookingMatch[1]
    return targetKind === 'circle'
      ? generatePath(CommunitiesRoutes.BookingShow, {
          slug: targetSlug,
          uuid: bookingId,
        })
      : generatePath(NetworksRoutes.BookingShow, {
          slug: targetSlug,
          uuid: bookingId,
        })
  }

  const materialEditMatch = pathname.match(
    /^\/pooling\/([^/]+)\/edit(?:\/([^/]+))?$/
  )
  if (materialEditMatch) {
    const materialSlug = materialEditMatch[1]
    const step = materialEditMatch[2]
    if (targetKind === 'circle') {
      if (step) {
        return generatePath(CommunitiesRoutes.MaterialEditStep, {
          communitySlug: targetSlug,
          materialSlug,
          step,
        })
      }
      return generatePath(CommunitiesRoutes.MaterialEdit, {
        communitySlug: targetSlug,
        materialSlug,
      })
    }
    if (targetKind === 'network') {
      if (step) {
        return generatePath(NetworksRoutes.MaterialEditStep, {
          networkSlug: targetSlug,
          materialSlug,
          step,
        })
      }
      return generatePath(NetworksRoutes.MaterialEdit, {
        networkSlug: targetSlug,
        materialSlug,
      })
    }
  }

  return null
}

const mapContextRouteToUserRoute = (pathname: string): string | null => {
  if (/^\/pooling\/[^/]+\/edit(?:\/[^/]+)?$/.test(pathname)) {
    return pathname
  }
  if (/^\/[cn]\/[^/]+\/settings(?:\/[^/]+)?$/.test(pathname)) {
    return UserRoutes.MyProfile
  }
  if (/^\/n\/[^/]+\/members(?:\/[^/]+)?$/.test(pathname)) {
    return NetworksRoutes.MyNetworks
  }
  if (/^\/c\/[^/]+\/members$/.test(pathname)) {
    return CommunitiesRoutes.MyCommunities
  }
  if (/^\/[cn]\/[^/]+\/materials\/add-from-templates$/.test(pathname)) {
    return PoolingRoutes.AddFromTemplates
  }
  if (/^\/[cn]\/[^/]+\/materials$/.test(pathname)) {
    return PoolingRoutes.MyMaterials
  }
  const materialEditFromContext = pathname.match(
    /^\/[cn]\/[^/]+\/materials\/([^/]+)\/edit(?:\/([^/]+))?$/
  )
  if (materialEditFromContext) {
    const materialSlug = materialEditFromContext[1]
    const step = materialEditFromContext[2]
    if (step) {
      return generatePath(PoolingRoutes.EditMyMaterialStep, {
        slug: materialSlug,
        step,
      })
    }
    return generatePath(PoolingRoutes.EditMyMaterial, { slug: materialSlug })
  }
  if (/^\/[cn]\/[^/]+\/bookings\/as-owner$/.test(pathname)) {
    return PoolingRoutes.MyBookingAsOwner
  }
  if (/^\/[cn]\/[^/]+\/bookings\/requests$/.test(pathname)) {
    return PoolingRoutes.MyBookingRequests
  }

  const bookingMatch = pathname.match(/^\/[cn]\/[^/]+\/bookings\/([^/]+)$/)
  if (bookingMatch?.[1]) {
    return generatePath(PoolingRoutes.MyBookingShow, { id: bookingMatch[1] })
  }

  return null
}

const mapContextRouteToContextRoute = (
  pathname: string,
  targetKind: ContextKind,
  targetSlug: string
): string | null => {
  // Network members may include an optional :communitySlug segment,
  // but community members route does not support this extra segment.
  if (/^\/n\/[^/]+\/members(?:\/[^/]+)?$/.test(pathname)) {
    if (targetKind === 'circle') {
      return generatePath(CommunitiesRoutes.Members, { slug: targetSlug })
    }
    if (targetKind === 'network') {
      return generatePath(NetworksRoutes.Members, {
        slug: targetSlug,
        communitySlug: null,
      })
    }
  }

  if (/^\/c\/[^/]+\/members$/.test(pathname)) {
    if (targetKind === 'network') {
      return generatePath(NetworksRoutes.Members, {
        slug: targetSlug,
        communitySlug: null,
      })
    }
    if (targetKind === 'circle') {
      return generatePath(CommunitiesRoutes.Members, { slug: targetSlug })
    }
  }

  return null
}

export const getContextSwitchTargetPath = ({
  currentPathname,
  targetContextIri,
}: {
  currentPathname: string
  targetContextIri: string | null
}): string => {
  const targetKind = getContextKind(targetContextIri)
  const targetSlug = getSlugFromContextIri(targetContextIri)

  if (targetKind === 'user') {
    const mappedUserRoute = mapContextRouteToUserRoute(currentPathname)
    return mappedUserRoute ?? StaticPagesRoutes.Home
  }

  const currentIsContextRoute = currentPathname.match(
    /^\/([cn])\/[^/]+(\/.*)?$/
  )
  if (currentIsContextRoute && targetSlug) {
    const mappedContextRoute = mapContextRouteToContextRoute(
      currentPathname,
      targetKind,
      targetSlug
    )
    if (mappedContextRoute) {
      return mappedContextRoute
    }

    const [, currentPrefix, currentSuffix = ''] = currentIsContextRoute
    const targetPrefix = getTargetContextRoutePrefix(targetKind)

    // Keep an equivalent contextual route when switching c<->n.
    if (
      (currentPrefix === 'c' && targetPrefix === '/n') ||
      (currentPrefix === 'n' && targetPrefix === '/c') ||
      (currentPrefix === 'c' && targetPrefix === '/c') ||
      (currentPrefix === 'n' && targetPrefix === '/n')
    ) {
      return `${targetPrefix}/${targetSlug}${currentSuffix}`
    }
  }

  const isPoolingMaterialEditPath = /^\/pooling\/[^/]+\/edit(?:\/[^/]+)?$/.test(
    currentPathname
  )
  if (currentPathname.startsWith('/my/') || isPoolingMaterialEditPath) {
    const mappedContextRoute = mapUserRouteToContextRoute(
      currentPathname,
      targetKind,
      targetSlug
    )
    if (mappedContextRoute) {
      return mappedContextRoute
    }
    // No /c/... or /n/... equivalent: keep the user-space URL (avoid redirect to dashboard).
    if (currentPathname === PoolingRoutes.MyAlerts) {
      return currentPathname
    }
  }

  return toTargetDashboard(targetKind, targetSlug)
}
